"use client";

import { useState } from "react";
import Image from "next/image";

type BlogHeroCProps = {
  variant: "editorial" | "dataviz" | "geometric";
  category: string;
};

const IMAGE_SRC: Record<BlogHeroCProps["variant"], string> = {
  editorial: "/blog-image-samples/c1-editorial.png",
  dataviz:   "/blog-image-samples/c2-dataviz.png",
  geometric: "/blog-image-samples/c3-geometric.png",
};

const VARIANT_LABEL: Record<BlogHeroCProps["variant"], string> = {
  editorial: "Editorial illustration",
  dataviz:   "Data visualisation",
  geometric: "Geometric abstract",
};

export function BlogHeroC({ variant, category }: BlogHeroCProps) {
  const [failed, setFailed] = useState(true);

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      role="img"
      aria-label={`${VARIANT_LABEL[variant]} for ${category} article`}
    >
      {/* Generated image: revealed only once it loads successfully */}
      <Image
        src={IMAGE_SRC[variant]}
        alt=""
        fill
        className="object-cover"
        style={{ display: failed ? "none" : undefined }}
        onLoad={() => setFailed(false)}
        onError={() => setFailed(true)}
        sizes="(max-width: 768px) 100vw, 50vw"
      />

      {/* Placeholder shown until Gemini billing is enabled */}
      {failed && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center"
          style={{
            background:
              "repeating-linear-gradient(45deg, var(--color-bg-sunken), var(--color-bg-sunken) 10px, white 10px, white 20px)",
          }}
        >
          <span className="inline-flex items-center gap-1.5 rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-widest text-emerald-700">
            ✦ Gemini · billing required
          </span>
          <span className="text-xs font-bold text-slate-800">{VARIANT_LABEL[variant]}</span>
        </div>
      )}

      {/* Category badge: always visible */}
      <div className="absolute left-4 top-4 z-10">
        <span className="inline-flex items-center rounded-sm border border-black/5 bg-white/90 px-3 py-1 text-xs font-bold uppercase tracking-widest text-emerald-700 shadow-sm backdrop-blur-md">
          {category}
        </span>
      </div>
    </div>
  );
}
